export type DurationUnit = 'm' | 'h' | 'd' | 'w';

const MS_PER_UNIT: Record<DurationUnit, number> = {
  m: 60_000,
  h: 3_600_000,
  d: 86_400_000,
  w: 604_800_000,
};

const UNITS_DESCENDING: DurationUnit[] = ['w', 'd', 'h', 'm'];

const DURATION_RE = /^(\d+)(m|h|d|w)$/;

/**
 * Parse a duration string like "3d" into milliseconds.
 * Returns 0 for invalid strings.
 */
export function parseDuration(str: string): number {
  const match = DURATION_RE.exec(str);
  if (!match) return 0;
  const amount = Number(match[1]);
  const unit = match[2] as DurationUnit;
  return amount * MS_PER_UNIT[unit];
}

/**
 * Convert milliseconds to a duration string like "3d", picking the largest
 * unit that divides evenly. Falls back to minutes.
 */
export function msToDuration(ms: number): string {
  if (ms <= 0) return '0m';
  for (const unit of UNITS_DESCENDING) {
    if (ms % MS_PER_UNIT[unit] === 0) {
      return `${ms / MS_PER_UNIT[unit]}${unit}`;
    }
  }
  return `${Math.round(ms / MS_PER_UNIT.m)}m`;
}

/**
 * Extract the numeric amount from a duration string.
 */
export function durationAmount(str: string): number {
  const match = DURATION_RE.exec(str);
  return match ? Number(match[1]) : 0;
}

/**
 * Extract the unit from a duration string.
 */
export function durationUnit(str: string): DurationUnit {
  const match = DURATION_RE.exec(str);
  return match ? (match[2] as DurationUnit) : 'm';
}

/**
 * Validate that a string is a well-formed duration.
 */
export function isValidDuration(str: string): boolean {
  return DURATION_RE.test(str);
}
